import { useState } from "react";
import { Plus, HelpCircle, CheckCircle2, Circle, Lightbulb } from "lucide-react";
import { Button } from "@component/button";
import { FormQuestion } from "@type/assessments";
import FormQuestionCard from "./form-question-card";
import InlineQuestionTypeSelector from "./inline-question-type-selector";

interface FormQuestionsSectionProps {
    questions: FormQuestion[];
    onQuestionsChange: (questions: FormQuestion[]) => void;
    disabled?: boolean;
    isLoadingAssessment?: boolean;
    showPoints?: boolean;
}

export default function FormQuestionsSection({
    questions,
    onQuestionsChange,
    disabled = false,
    isLoadingAssessment = false,
    showPoints = false,
}: FormQuestionsSectionProps) {
    const [isQuestionTypeSelectorOpen, setIsQuestionTypeSelectorOpen] = useState(false);

    const typeMapping: Record<string, FormQuestion["type"]> = {
        choice: "Multiple Choice",
        text: "Short Answer",
        rating: "Rating Scale",
    };

    const addQuestion = (questionType?: string) => {
        const mappedType: FormQuestion["type"] = questionType
            ? typeMapping[questionType] || "Short Answer"
            : "Short Answer";

        const newQuestion: FormQuestion = {
            id: `q_${Date.now().toString()}`,
            type: mappedType,
            content: "",
            options: mappedType === "Multiple Choice" ? ["", ""] : [],
        };

        if (mappedType === "Rating Scale") {
            newQuestion.min_rate = 1;
            newQuestion.max_rate = 5;
            newQuestion.min_label = "Low";
            newQuestion.max_label = "High";
        }

        onQuestionsChange([...questions, newQuestion]);
    };

    const handleQuestionTypeSelect = (typeId: string) => {
        addQuestion(typeId);
        setIsQuestionTypeSelectorOpen(false);
    };

    const duplicateQuestion = (questionIndex: number) => {
        const questionToDuplicate = questions[questionIndex];
        if (questionToDuplicate) {
            const newQuestion: FormQuestion = {
                ...questionToDuplicate,
                id: `q_${Date.now().toString()}`,
            };
            const newQuestions = [...questions];
            newQuestions.splice(questionIndex + 1, 0, newQuestion);
            onQuestionsChange(newQuestions);
        }
    };

    const moveQuestionUp = (questionIndex: number) => {
        if (questionIndex > 0) {
            const newQuestions = [...questions];
            [newQuestions[questionIndex - 1], newQuestions[questionIndex]] = [
                newQuestions[questionIndex],
                newQuestions[questionIndex - 1],
            ];
            onQuestionsChange(newQuestions);
        }
    };

    const moveQuestionDown = (questionIndex: number) => {
        if (questionIndex < questions.length - 1) {
            const newQuestions = [...questions];
            [newQuestions[questionIndex], newQuestions[questionIndex + 1]] = [
                newQuestions[questionIndex + 1],
                newQuestions[questionIndex],
            ];
            onQuestionsChange(newQuestions);
        }
    };

    const removeQuestion = (questionIndex: number) => {
        onQuestionsChange(questions.filter((_, i) => i !== questionIndex));
    };

    const updateQuestion = (questionIndex: number, field: string, value: any) => {
        const updatedQuestions = questions.map((q, i) => {
            if (i === questionIndex) {
                if (field === "type" && value === "Rating Scale") {
                    return {
                        ...q,
                        type: value as "Rating Scale",
                        min_rate: 1,
                        max_rate: 5,
                        min_label: "Low",
                        max_label: "High",
                    };
                }
                return { ...q, [field]: value };
            }
            return q;
        });
        onQuestionsChange(updatedQuestions);
    };

    const addOption = (questionIndex: number) => {
        const updatedQuestions = questions.map((q, i) => {
            if (i === questionIndex) {
                return { ...q, options: [...(q.options || []), ""] };
            }
            return q;
        });
        onQuestionsChange(updatedQuestions);
    };

    const removeOption = (questionIndex: number, optionIndex: number) => {
        const updatedQuestions = questions.map((q, i) => {
            if (i === questionIndex) {
                return {
                    ...q,
                    options: (q.options || []).filter((_, idx) => idx !== optionIndex),
                };
            }
            return q;
        });
        onQuestionsChange(updatedQuestions);
    };

    const updateOption = (questionIndex: number, optionIndex: number, value: string) => {
        const updatedQuestions = questions.map((q, i) => {
            if (i === questionIndex) {
                return {
                    ...q,
                    options: (q.options || []).map((opt, idx) =>
                        idx === optionIndex ? value : opt
                    ),
                };
            }
            return q;
        });
        onQuestionsChange(updatedQuestions);
    };

    const updateRatingScale = (questionIndex: number, field: string, value: any) => {
        const updatedQuestions = questions.map((q, i) => {
            if (i === questionIndex) {
                return { ...q, [field]: value };
            }
            return q;
        });
        onQuestionsChange(updatedQuestions);
    };

    const checklistItems = [
        {
            label: "Add at least 1 question",
            completed: questions.length >= 1,
        },
    ];

    const completedCount = checklistItems.filter((item) => item.completed).length;
    const totalCount = checklistItems.length;
    const progressPercentage = Math.round((completedCount / totalCount) * 100);

    return (
        <div
            className="pb-8 border-b border-gray-200"
            style={{ paddingTop: "24px", paddingBottom: "40px" }}
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Panel */}
                <div className="lg:col-span-3 flex flex-col space-y-4">
                    <div className="space-y-3">
                        <div
                            className="flex items-start justify-between gap-3"
                            style={{ paddingRight: "16px" }}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="flex items-center justify-center w-10 h-10 rounded-lg"
                                    style={{ backgroundColor: "#dbeafe", color: "#2563eb" }}
                                >
                                    <HelpCircle
                                        className="w-5 h-5"
                                        style={{ color: "#2563eb" }}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <h2 className="text-lg font-semibold">Form Questions</h2>
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                        <span>
                                            {questions.length}{" "}
                                            {questions.length === 1 ? "question" : "questions"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <span
                                className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded"
                                style={{
                                    paddingTop: "4px",
                                    paddingBottom: "4px",
                                    borderRadius: "1rem",
                                }}
                            >
                                Step 2
                            </span>
                        </div>
                    </div>
                    <p className="text-sm text-gray-500">
                        Add and configure questions for your form.
                        <br />
                        <br />
                        You can add different types of questions to gather the information you need.
                    </p>

                    {/* Tip Banner */}
                    <div
                        className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg"
                        style={{
                            marginTop: "16px",
                            marginBottom: "16px",
                            paddingTop: "12px",
                        }}
                    >
                        <div className="flex gap-2">
                            <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-900">
                                <span className="font-semibold">Tip:</span> A well-designed form
                                usually contains 5 to 10 relevant questions. Focus on
                                clarity, job relevance, and decision-making impact.
                            </p>
                        </div>
                    </div>

                    <div className="flex-1" />

                    {/* Progress Checklist */}
                    <div className="mt-auto space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">
                                Complete your Form Questions
                            </h3>
                            <span className="text-xs text-blue-600">
                                {completedCount}/{totalCount} completed
                            </span>
                        </div>

                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>

                        <div className="space-y-2.5 pt-1">
                            {checklistItems.map((item, index) => (
                                <div key={index} className="flex items-start gap-2.5">
                                    <div className="mt-0.5">
                                        {item.completed ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                                        ) : (
                                            <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                        )}
                                    </div>
                                    <span
                                        className={`text-sm leading-tight ${item.completed
                                            ? "text-gray-700"
                                            : "text-gray-500"
                                            }`}
                                    >
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Panel: Questions List */}
                <div className="lg:col-span-9 space-y-6">
                    {showPoints && questions.length > 0 && (
                        <div className="flex items-center gap-3">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                                <span>Total Points:</span>
                                <span>
                                    {questions.reduce((sum, q) => sum + (q.points || 0), 0)}
                                </span>
                            </div>
                        </div>
                    )}
                    {!isLoadingAssessment && (
                        <div className="space-y-1">
                            {questions.map((question, index) => (
                                <div key={question.id}>
                                    {index > 0 && <div className="border-t border-gray-200 my-6" />}
                                    <FormQuestionCard
                                        question={question}
                                        index={index}
                                        showPoints={showPoints}
                                        onUpdate={updateQuestion}
                                        onDelete={removeQuestion}
                                        onDuplicate={duplicateQuestion}
                                        onMoveUp={moveQuestionUp}
                                        onMoveDown={moveQuestionDown}
                                        canMoveUp={index > 0}
                                        canMoveDown={index < questions.length - 1}
                                        disabled={disabled}
                                        onAddOption={addOption}
                                        onRemoveOption={removeOption}
                                        onUpdateOption={updateOption}
                                        onUpdateRatingScale={updateRatingScale}
                                    />
                                </div>
                            ))}

                            {/* Add Question Button */}
                            {!disabled && (
                                <div className="space-y-4">
                                    <div className="flex justify-center pt-4">
                                        <Button
                                            onClick={() =>
                                                setIsQuestionTypeSelectorOpen(!isQuestionTypeSelectorOpen)
                                            }
                                            variant="outline"
                                            size="sm"
                                            className="flex items-center gap-2"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Add Question
                                        </Button>
                                    </div>

                                    {isQuestionTypeSelectorOpen && (
                                        <InlineQuestionTypeSelector
                                            onSelectType={handleQuestionTypeSelect}
                                            onCancel={() => setIsQuestionTypeSelectorOpen(false)}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
